import {
    ActivityStatus,
    DonationMatchPolicyStatus,
    OrgStatus,
    RegistrationStatus,
    ScheduledActivityRegistrationRestriction
} from '@fieldday/fielddayportal-model'
import { Alert, AlertTitle, Box, Button, Divider, Grid, Paper, Skeleton, Stack, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import _ from 'lodash'
import qs from 'qs'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useNonprofitContext } from '../../hooks/commonOrgContext'
import { useTeamRoleContext } from '../../hooks/teamRoleContext'
import { useAuth } from '../../hooks/useAuth'
import { useLoading } from "../../hooks/useLoading"
import { UploadProps } from '../../hooks/useProcessImage'
import { useScheduledActivityContext } from '../../hooks/useScheduledActivityContext'
import { useScheduledActivityImagesContext } from '../../hooks/useScheduledActivityImagesContext'
import { useScheduledActivityMessagesContext } from '../../hooks/useScheduledActivityMessagesContext'
import { ScheduledActivityForView, teamScheduledActivity } from '../../models/Activity'
import { Campaign } from '../../models/Campaign'
import { DonationMatchPolicy } from '../../models/Donations'
import { OrgUnit } from '../../models/OrgUnit'
import { getEventRegStatus } from '../../models/Registration'
import { UserHelper } from '../../models/UserHelper'
import useGlobalStyles from '../../styles/globalStyles'
import { appBarHeight } from '../../styles/navBarStyles'
import useOrgStyles from '../../styles/orgStyles'
import theme from '../../styles/theme'
import useStyles from '../../styles/useStyles'
import { platformDonationsEnabled } from '../../util/featureFlags'
import { pathToBannerPlaceholder } from '../../util/icons'
import { resizedImageUrl } from '../../util/resizedImageUrl'
import { WEBIP_CURRENT_ID, detailsParticipants, isNonVolunteerEvent, isVolunteerEvent, regPeople, regRemain } from '../../util/scheduledActivityUtils'
import { useAPI } from '../../util/useAPI'
import SignInButton from '../Accounts/SignInButton'
import ContentContainer from '../Global/ContentContainer'
import Header from '../Global/Header'
import { StyledMarkdown } from '../Global/StyledMarkdown'
import LocationPreviewBox from '../Locations/LocationPreviewBox'
import ScheduledActivityMessages from '../Messages/ScheduledActivityMessages'
import JoinOrgButton from '../OrgCommon/JoinOrgButton'
import { MainTab, useTabStyles } from '../OrgCommon/TabHelpers'
import ActivityPhotoAndLogos from './ActivityPhotoAndLogos'
import ContactBox from './ContactBox'
import ScheduledActivityDetailsBox from './ScheduledActivityDetailsBox'
import ScheduledActivityDonateBox from './ScheduledActivityDonateBox'

const RegistrantsBox = React.lazy(() => import('./RegistrantsBox'))
const ReocurringScheduledActivity = React.lazy(() => import('./ReoccurringScheduledActivity'))
const RsvpBox = React.lazy(() => import('./RsvpBox'))
const ScheduledActivityManagementBox = React.lazy(() => import('./ScheduledActivityManagementBox'))
const ScheduledActivityPhotoswipe = React.lazy(() => import('./ScheduledActivityPhotoswipe'))
const ScheduledActivityStructuredData = React.lazy(() => import('./ScheduledActivityStructuredData'))
const SurveyBox = React.lazy(() => import('./SurveyBox'))
const TeamContactBox = React.lazy(() => import('./TeamContactBox'))
const CampaignBanner = React.lazy(() => import('../Campaigns/CampaignBanner'))

export function SASkeleton() {
  return (
    <ContentContainer>
      <Skeleton variant="rectangular" height={70} />
      <Skeleton variant="rectangular" height={70} />
      <Skeleton variant="rectangular" height={70} />
    </ContentContainer>
  )
}

type VisibleSection = {
  name: string
  ref: React.RefObject<HTMLDivElement | null>
  ratio: number
  clicked: boolean
}
export default function ScheduledActivityContent(props: {
  brief?: boolean
  activityImageUploadProps?: UploadProps,
}) {
  const { brief, activityImageUploadProps } = props
  const { canCoordinate, isMember: isNpoMember } = useNonprofitContext()
  const { scheduledActivity: sa, registrations, isPastEnd, orgUnitShares, coordinatorOfSharingTeams,
    allSharingTeams, refreshScheduledActivity, ouIdForTeamMessagesAndPhotos } = useScheduledActivityContext()
  const globalStyles = useGlobalStyles()
  const npoStyles = useOrgStyles()
  const { user, isWizard } = useAuth()
  const { canCoordinate: canCoordinateTeam, isMember } = useTeamRoleContext()
  const { scheduledActivityMessages, messagesLoaded, canSeeMessages } = useScheduledActivityMessagesContext()
  const { imagesLoaded, canSeePhotos, canUploadPhotos } = useScheduledActivityImagesContext()
  const tabStyles = useTabStyles()
  const styles = useStyles()
  const theme = useTheme()
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'))
  const mdView = useMediaQuery(theme.breakpoints.down('lg'))

  const hasManagementTools = canCoordinate() || canCoordinateTeam() || coordinatorOfSharingTeams.length > 0
  const FieldDayAPI = useAPI()
  const [otherSchedActs, setOtherSchedActs] = useState<ScheduledActivityForView[] | null>(null)
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null)
  const [donationPolicy, setDonationPolicy] = useState<DonationMatchPolicy | null>(null)
  const { loadStart, loadEnd } = useLoading()

  const [activeSection, setActiveTabSection] = useState<string>("overview")
  const observerRef = useRef<IntersectionObserver | null>(null)
  const overviewRef = useRef<HTMLDivElement | null>(null)
  const volunteersRef = useRef<HTMLDivElement | null>(null)
  const photosRef = useRef<HTMLDivElement | null>(null)
  const messagesRef = useRef<HTMLDivElement | null>(null)
  const otherDatesRef = useRef<HTMLDivElement | null>(null)
  const managementRef = useRef<HTMLDivElement | null>(null)
  const donateRef = useRef<HTMLDivElement | null>(null)
  const { pathname, hash } = useLocation()
  const visibleSectionsDefaults: VisibleSection[] = [
    { name: "overview", ref: overviewRef, ratio: 0, clicked: hash === "#overview" },
    { name: "volunteers", ref: volunteersRef, ratio: 0, clicked: hash === "#volunteers" },
    { name: "photos", ref: photosRef, ratio: 0, clicked: hash === "#photos" },
    { name: "messages", ref: messagesRef, ratio: 0, clicked: hash === "#messages" },
    { name: "donate", ref: donateRef, ratio: 0, clicked: hash === "#donate" },
    { name: "otherDates", ref: otherDatesRef, ratio: 0, clicked: hash === "#otherDates" },
  ]
  const [visibleSections, setVisibleSections] = useState<VisibleSection[]>(visibleSectionsDefaults)
  const sectionRefs = [overviewRef, volunteersRef, photosRef, messagesRef, otherDatesRef, managementRef]
  const scrollMarginOptions = { sx: { scrollMarginTop: `calc(${appBarHeight} + 84px)` }}
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'))
  const hasOtherDates = otherSchedActs && otherSchedActs.length > 0
  const teamSa = teamScheduledActivity(sa)
  const preview = teamSa?.isPublicTeamFieldTrip && !isMember()
  const showParticipantSection: boolean = !isNonVolunteerEvent(sa) || allSharingTeams.length > 0 || canCoordinate()
  const showDonateSection = platformDonationsEnabled(teamSa?.orgUnit?.flags) && donationPolicy


  function scrollTo(sectionName: string) {
    const section = visibleSections.find(vs => vs.name === sectionName)
    const ref = section?.ref
    if (!section || !ref?.current) return
    if (smallScreen && sectionName === "overview") return // avoid jarring load on mobile

    // set all clicked to false unless it matches this one
    setVisibleSections(visibleSects => {
      visibleSects.forEach(vs => vs.clicked = vs === section)
      return visibleSects
    })

    // scroll to the header to make things line up right
    const el = ref.current.firstElementChild
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })

    setTimeout(() => {
      // remove the "clicked" weight
      setVisibleSections(visibleSects => {
        visibleSects.forEach(vs => vs.clicked = false)
        return visibleSects
      })
    }, 1500)
  }

  function initObserver() {
    sectionRefs.forEach(sectionRef => {
      if (sectionRef.current && observerRef.current) observerRef.current.observe(sectionRef.current)
    })
  }

  function setActiveSection(section: string) {
    setActiveTabSection(section)
    const hash = section.length > 0 ? `#${section}` : ""
    history.replaceState(null, "", hash)
  }

  const updateSections: () => void = useCallback(() => {
    // sort, add extra weight if the tab was clicked
    const sortedSections = _.sortBy(visibleSections, (vs) => 1 - vs.ratio - (vs.clicked ? 10 : 0))
    setActiveSection(sortedSections[0].name)
  }, [visibleSections])

  useEffect(() => {
    updateSections()
  }, [updateSections])

  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    // entries will only contain what has changed, so reset everything to ratio 0 and then update
    const sections = _.clone(visibleSectionsDefaults)
    entries.forEach((e: IntersectionObserverEntry) => {
      const ref = sectionRefs.find(r => e.target === r.current)
      const sec = sections.find(s => s.ref === ref)
      if (sec) sec.ratio = e.intersectionRatio
    })
    setVisibleSections(sections)
  }, [])

  useEffect(() => {
    loadStart()
    FieldDayAPI.listScheduledActivities({
      actId: sa.activityId,
      status: "PUBLISHED",
      past: false
    }).then(resp => {
      const filtered = resp.filter(acts => acts.id !== sa.id)
      setOtherSchedActs(filtered)
    })
      .finally(loadEnd)
  }, [])

  useEffect(() => {
    if (user?.id) {
      FieldDayAPI.getScheduledActivityWithAuth(sa.id).then(sa => {
        setCampaigns(sa.campaigns)
      })
    }
  }, [user?.id])

  useEffect(() => {
    if (!teamSa?.orgUnit) return
    const policyListStartDate = dayjs().startOf('year').format('YYYY-MM-DD')
    const policyListEndDate = dayjs().format('YYYY-MM-DD')
    FieldDayAPI.listDonationMatchPolicies(teamSa?.orgUnit?.id, policyListStartDate, policyListEndDate, DonationMatchPolicyStatus.Active)
    .then((resp) => {
      const policies = resp.data.donationMatchPolicies
      setDonationPolicy(policies[0])
    }).catch(err => {
      console.warn("Error while fetching donation match policies", err)
    })
  }, [teamSa?.orgUnit?.id])

  useEffect(() => {
    // give messages and images a chance to load before trying to scroll
    if (!hash || !messagesLoaded || !imagesLoaded) return
    const section = hash.replace('#', '')

    // give it a few ms to load the DOM before scrolling for the first time
    setTimeout(() => {
      setActiveSection(section)
      scrollTo(section)

      setTimeout(initObserver, 500)
    }, 500)
  }, [pathname, hash, messagesLoaded, imagesLoaded])

  const query = qs.parse(location.search, { ignoreQueryPrefix: true })
  useEffect(() => {
    if (user) {
      localStorage.removeItem("returnTo")
    }
  }, [user?.id, query])

  observerRef.current = new IntersectionObserver(handleObserver, {
    root: null,
    rootMargin: "-224px 0px 0px 0px",
    threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
  })

  const displayCampaign = (orgUnit: OrgUnit) => {
    if (orgUnitShares) {
      const campaignsToDisplay = campaigns?.filter(campaign => campaign.orgUnitId === orgUnit.id)
      return campaignsToDisplay?.map(campaign => <CampaignBanner key={campaign.id} campaign={campaign} orgUnit={orgUnit} eventPage />)
    } else {
      return campaigns?.map(campaign => <CampaignBanner key={campaign.id} campaign={campaign} orgUnit={orgUnit} eventPage />)
    }
  }

  // ensure that there is an activity
  if (!sa.activity) {
    if (brief) return (
      <Typography className={globalStyles.loadingText}>Loading activity ...</Typography>
    )
    else return (
      <Typography>An unexpected error occured while loading the activity.</Typography>
    )
  }

  const activityImageProps = {
    component: "img",
    className: npoStyles.bannerImage,
    image: activityImageUploadProps?.imageUrl || resizedImageUrl(sa.activity.bannerImage, 700, 550) || pathToBannerPlaceholder,
    sx: { mb: 0 },
  }

  const myReg = registrations.find(r => r.userId === user?.id)
  const confirmationStatus = getEventRegStatus(myReg, sa)
  const shareToTeamHelptText = confirmationStatus === "Pending" ?
    "Your registration is pending and you are currently signed up for this event as an individual."
    : "You are currently registered for this event as an individual."

  const slotsRemainingView = !isPastEnd && isVolunteerEvent(sa) ?
    <Typography color="textSecondary" textAlign="center" mt={mobileView ? 1 : 2} mb={mobileView ? 2 : 3}>
      {regRemain(sa)}
    </Typography> :
    <Box mt={2} mb={2}></Box>

  // NOTE: photos and messages require you to be part of the Team (FT) or registered (Comm Event)
  const photoView = () => {
    if (canSeePhotos) {
      if (sa.status === ActivityStatus.Published) return <React.Suspense><ScheduledActivityPhotoswipe orgUnitId={ouIdForTeamMessagesAndPhotos}
        canUpload={canUploadPhotos} /></React.Suspense>
      if (sa.status === ActivityStatus.Canceled) return <React.Suspense><ScheduledActivityPhotoswipe canUpload={false} /></React.Suspense>
    } else {
      if (sa.status === ActivityStatus.Published) return <Typography>Register for the event to view and upload photos</Typography>
    }
    return null
  }

  const messagesView = () => {
    const postingHelpText = isNonVolunteerEvent(sa) ? "announcements" : "and post messages"
    if (canSeeMessages) {
      switch (sa.status) {
        case ActivityStatus.Draft: return (
          <Typography color="textSecondary" mb={7}>
            Message board will become available when event is published
          </Typography>
        )
        case ActivityStatus.Published: return (
          <Box mt={3} mb={7}>
            <ScheduledActivityMessages sa={sa}
              disableMessage={(isNonVolunteerEvent(sa) || sa.id === WEBIP_CURRENT_ID) && !isWizard() && !isNpoMember()}
              orgUnitId={ouIdForTeamMessagesAndPhotos} />
          </Box>
        )
        case ActivityStatus.Canceled: return (
          !scheduledActivityMessages || scheduledActivityMessages.length === 0 ?
            <Typography color="textSecondary" mb={7}>
              No messages have been sent.
            </Typography>
            :
            <Box mt={3} mb={7}>
              <ScheduledActivityMessages sa={sa} disableMessage />
            </Box>
        )
      }
    } else {
      if (sa.status === ActivityStatus.Published) return <Typography mb={7}>Register for the event to view {postingHelpText}</Typography>
    }
    return null
  }

  const joinTeamButton = (label: string) => {
    return (
      <Button variant='contained' component={Link} to={`/teams/${sa.orgUnit?.handle}`}>
        {label}
      </Button>
    )
  }
  const signInHelpText = () => {
    if (teamSa?.isPublicTeamFieldTrip) return user ?
      `Join ${sa.orgUnit?.name} to view participants, messages, and photos related to this event.` :
      `Sign in or create an account to join ${sa.orgUnit?.name} and view participants, messages, and photos related to this event.`
    // community event messaging
    if (!user) return ('Sign in or create an account to view messages and photos related to this event.')
  }

  const signInOrJoinBtns = (brief?: boolean) => {
    if (user) return <JoinOrgButton orgType='orgunit' orgUnit={sa.orgUnit} labelOverride="Join Team to register"
      refreshPage={() => {
        refreshScheduledActivity()
      }} />
    if (teamSa?.isPublicTeamFieldTrip && !user) return joinTeamButton(brief ? " Join Team" : "Sign in to register")
    if (!user) return <SignInButton> {brief ? "Sign in" : "Sign in to register"}</SignInButton>
    return null
  }

  const draftAlertText = () => {
    if (isPastEnd) {
      return (
        <>
          <Typography variant="body2" >
            This draft is for an event that is in the past.
          </Typography>
          {canCoordinateTeam() &&
            <Typography variant="body2" >You may update registrations for tracking purposes after this event is published.</Typography>
          }
        </>
      )
    }
    return (
      <>
        <Typography variant="body2" >
          {canCoordinate() ? 'Volunteers can only register when the event is published.' : 'Check back later to register for this event.'}
        </Typography>
        {canCoordinateTeam() &&
          <Typography variant="body2" >Team invites and guest links will be available when the event is published.</Typography>
        }
      </>
    )
  }

  if (preview || !user) return (
    <>
      <React.Suspense><ScheduledActivityStructuredData /></React.Suspense>
      <ContentContainer>
        <Header primaryHeader={sa.activity.name} subheader={`${sa.nonProfitOrg?.name}${sa.orgUnit ? ` + ${sa.orgUnit.name}` : ""}`} withDivider />
        <Grid container >
          {/* LEFT SIDE*/}
          <Grid item xs={12} md={7} mt={mobileView ? 0 : 3}>
            {/* ACTIVITY PHOTO AND LOGOS */}
            <Grid container columns={7} alignItems="center" >
              <ActivityPhotoAndLogos activityImageProps={activityImageProps} />
            </Grid>
            {/* DESCRIPTION & KEY DETAILS */}
            <ScheduledActivityDescriptions sa={sa} />
            <Divider sx={{ my: mobileView ? 4 : 6 }} />
            <Stack direction={mobileView ? "column" : "row"} alignItems="center" spacing="1em" marginBottom={mobileView ? 5 : 7}>
              <Typography>
                {signInHelpText()}
              </Typography>
              {signInOrJoinBtns(true)}
            </Stack>
            {/* REOCCURING PUBLIC EVENT LIST */}
            {hasOtherDates && !teamSa?.isPublicTeamFieldTrip &&
              <React.Suspense>
                <Box ref={otherDatesRef}>
                  <ReocurringScheduledActivity schedActs={otherSchedActs} />
                </Box>
              </React.Suspense>
            }
          </Grid>
          {/* SPACER */}
          <Grid item xs={0} md={1} />
          {/* RIGHT SIDE */}
          <Grid item xs={12} md={4} sx={{ order: smallScreen ? -1 : 0, mb: 2 }}>
            <Box sx={{ position: 'sticky', top: `calc(${appBarHeight} + 5.5rem)` }}>
              <Box sx={{
                marginTop: mobileView ? 0 : "1rem",
                padding: mobileView ? "1.25rem" : "1.5rem",
                backgroundColor: theme.palette.neutral.light,
                borderTopRightRadius: "4px",
                borderTopLeftRadius: "4px"
              }}>
                <Box sx={{ display: 'flex' }} justifyContent="center">
                  {signInOrJoinBtns()}
                </Box>
                {slotsRemainingView}
                <ScheduledActivityDetailsBox />
              </Box>
              {sa.location && !smallScreen && <LocationPreviewBox location={sa.location} eventPage />}
            </Box>
          </Grid>
        </Grid>
      </ContentContainer>
    </>
  )

  return (
    <ContentContainer>
      <Header primaryHeader={sa.activity.name} subheader={`${sa.nonProfitOrg?.name}${sa.orgUnit ? ` + ${sa.orgUnit.name}` : ""}`} withDivider />
      {sa.status === ActivityStatus.Draft &&
        <Box >
          <Alert severity="warning" sx={{ mt: "1.5em" }} >
            <AlertTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {canCoordinate() ? 'Draft—do not share link' : 'Draft—not ready for registrations'}
            </AlertTitle>
            {draftAlertText()}
          </Alert>
        </Box>
      }

      {sa.status === ActivityStatus.Canceled &&
        <Alert severity="warning" sx={{ mt: "1.5em", fontSize: "1em", fontWeight: 600, alignItems: "center" }}>
         <AlertTitle> This event has been canceled.</AlertTitle>
         <Typography variant='body2'><span style={{ fontWeight: 500 }}>Reason provided:</span> {sa.reasonForCancel}</Typography>
        </Alert>
      }

      <Grid container>

        {/* ANCHOR TABS */}
        {!mobileView &&
          <Grid item xs={12} mb={smallScreen ? 2 : 0}
            style={{
              position: smallScreen ? "static" : "sticky",
              top: `calc(${appBarHeight} + ${theme.spacing(mdView ? .6 : 1.7)}`,
              backgroundColor: theme.palette.background.default,
              zIndex: 1000,
              order: -2,
            }}>
            <Box >
              <Tabs
                className={tabStyles.eventSectionHorizontalTabs}
                value={activeSection}
              >
                <MainTab label="Overview" value="overview"
                  onClick={() => {
                    setActiveSection("overview")
                    scrollTo("overview")
                  }}
                />
                {showParticipantSection &&
                  <MainTab label={regPeople(sa, true)} value="volunteers"
                    onClick={() => {
                      setActiveSection("volunteers")
                      scrollTo("volunteers")
                    }}
                  />
                }
                {!isNonVolunteerEvent(sa) &&
                  <MainTab label="Photos" value="photos" onClick={() => {
                    setActiveSection("photos")
                    scrollTo("photos")
                  }}
                  />
                }
                <MainTab label="Messages" value="messages"
                  onClick={() => {
                    setActiveSection("messages")
                    scrollTo("messages")
                  }}
                />
                {showDonateSection &&
                  <MainTab label="Donate" value="donate"
                    onClick={() => {
                      setActiveSection("donate")
                      scrollTo("donate")
                    }}
                  />
                }
                {hasOtherDates &&
                  <MainTab label="Other dates" value="otherDates"
                    onClick={() => {
                      setActiveSection("otherDates")
                      scrollTo("otherDates")
                    }}
                  />
                }
              </Tabs>
            </Box>
          </Grid>
        }

        {/* LEFT SIDE*/}
        <Grid item xs={12} md={7} mt={mobileView ? 0 : 3}>

          {/* OVERVIEW */}
          <Box ref={overviewRef}>
            <Grid container columns={7} alignItems="center" {...scrollMarginOptions}>
              {orgUnitShares && orgUnitShares.length > 0 &&
                myReg && !myReg.orgUnitId && myReg.status !== RegistrationStatus.Withdrawn && (
                  <Alert severity="warning" style={{ marginBottom: "1em" }}>
                    {shareToTeamHelptText} To change your affiliation
                    to {orgUnitShares.map(ous => ous.orgUnit.name).join(" or ")},
                    click the <strong>Edit registration</strong> button.
                  </Alert>
                )}
              {/* TEAM SHARE OR CAMPAIGN BANNER */}
              {campaigns?.length && campaigns?.length > 0 ?
                <Grid item xs={7}>
                  {sa.orgUnit ? displayCampaign(sa.orgUnit) : orgUnitShares?.map(ous => displayCampaign(ous.orgUnit))}
                </Grid>
                :
                orgUnitShares?.map(ous => {
                  const sharingUserHelper = ous.sharingUser ? new UserHelper(ous.sharingUser) : undefined
                  return (
                    <Grid item xs={7} key={ous.id}>
                      <Paper className={styles.boxContentTight} elevation={0}>
                        <Grid container spacing={1.5}>
                          {(ous.orgUnit.logo && !mobileView) &&
                            <Grid item xs={2}>
                              <img src={resizedImageUrl(ous.orgUnit.logo, 100, 50)} className={`${npoStyles.logoImage} mini`} />
                            </Grid>
                          }
                          <Grid item xs={ous.orgUnit.logo && !mobileView ? 10 : 12}>
                            {myReg && myReg.orgUnitId === ous.orgUnitId && myReg.status !== RegistrationStatus.Withdrawn ?
                              <Typography variant="subhead3B" mb={1}>
                                Field Trip with {ous.orgUnit.name}
                              </Typography>
                              :
                              <Typography variant="subhead3B" mb={1}>
                                This event has been shared as a Field Trip with {ous.orgUnit.name}.
                              </Typography>
                            }
                            <Typography variant="label3">
                              Message from {sharingUserHelper?.displayName() ?? "your Team"}
                            </Typography>
                            <StyledMarkdown>
                              {ous.message}
                            </StyledMarkdown>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  )
                })}
              {/* ACTIVITY PHOTO AND LOGOS */}
              <ActivityPhotoAndLogos activityImageProps={activityImageProps} />
            </Grid>

            {/* DESCRIPTION & KEY DETAILS */}
            <ScheduledActivityDescriptions sa={sa} />
          </Box>

          {/* POINTS OF CONTACT */}
          <Typography variant="subhead2B" mt={mobileView ? 2.5 : 3.5} mb={mobileView ? .75 : 1.25}>Point of contact</Typography>
          <ContactBox registrations={registrations} />
          {(sa.status !== ActivityStatus.Canceled || sa.nonProfitOrg?.status === OrgStatus.Placeholder) &&
            <React.Suspense><TeamContactBox /></React.Suspense>
          }
          <Divider sx={{ my: mobileView ? 4 : 6 }} />

          {/* REGISTERED VOLUNTEERS / PARTICIPANTS */}
          {showParticipantSection && <>
            <Box ref={volunteersRef}>
              <Typography variant="h4" {...scrollMarginOptions}>
                {regPeople(sa, true)}
              </Typography>
              <React.Suspense>
                <RegistrantsBox />
              </React.Suspense>
            </Box>
            <Divider sx={{ my: mobileView ? 4 : 6 }} />
          </>}

          {/* PHOTOS */}
          {
            <>
              <Box ref={photosRef}>
                <Typography variant="h4" {...scrollMarginOptions}>
                  Photos
                </Typography>
                {sa?.status === ActivityStatus.Draft &&
                  <Typography color="textSecondary">
                    Photo uploads will become available when event is published
                  </Typography>
                }
                {photoView()}
              </Box>
              <Divider sx={{ my: mobileView ? 4 : 6 }} />
            </>
          }

          {/* MESSAGES */}
          <Box ref={messagesRef}>
            <Typography variant="h4" ref={messagesRef} {...scrollMarginOptions}>
              {isNonVolunteerEvent(sa) ? "Announcements" : "Message board"}
            </Typography>
            {messagesView()}
          </Box>

          {/* DONATE */}
          {showDonateSection &&
            <>
              <Divider sx={{ my: mobileView ? 4 : 6 }} />
              <Box ref={donateRef}>
                <Typography variant="h4" ref={donateRef} {...scrollMarginOptions}>
                  Donate
                </Typography>
                <ScheduledActivityDonateBox matchPolicy={donationPolicy}/>
              </Box>
            </>
          }

          {/* REOCCURING PUBLIC EVENT LIST */}
          {hasOtherDates &&
            <Box ref={otherDatesRef}>
              <ReocurringScheduledActivity schedActs={otherSchedActs} />
            </Box>
          }
        </Grid>

        {/* SPACER */}
        <Grid item xs={0} md={1} />

        {/* RIGHT SIDE */}
        <Grid item xs={12} md={4} sx={{ order: smallScreen ? -1 : 0, mb: 2 }}>
          <Box sx={{ position: 'sticky', top: `calc(${appBarHeight} + 5.5rem)` }}>
            {hasManagementTools && <React.Suspense><ScheduledActivityManagementBox /></React.Suspense>}
            <Box sx={{
              padding: mobileView ? "1.25rem" : "1.5rem",
              backgroundColor: theme.palette.neutral.light,
              borderTopRightRadius: "4px",
              borderTopLeftRadius: "4px"
            }}>
              {(isWizard() && sa.config?.registrationRestriction === ScheduledActivityRegistrationRestriction.TeamShareOnly) &&
                <Alert severity='warning'>
                  Wizard notice: This event is restricted to team shares only
                </Alert>
              }
              <React.Suspense><RsvpBox /></React.Suspense>
              <React.Suspense><SurveyBox /></React.Suspense>
              {slotsRemainingView}
              <ScheduledActivityDetailsBox />
            </Box>
            {sa.location && !smallScreen && <LocationPreviewBox location={sa.location} eventPage />}
          </Box>
        </Grid>
      </Grid>
    </ContentContainer>
  )
}

function ScheduledActivityDescriptions({ sa }: { sa: ScheduledActivityForView }) {
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'))
  if (!sa.activity) return <Typography>An unexpected error occured while loading the activity.</Typography>
  return (
    <>
      <Box mt={mobileView ? 3 : 5}>
        {sa.activity.detailFields?.whatVolunteersDo &&
          <StyledMarkdown>{sa.activity.detailFields.whatVolunteersDo}</StyledMarkdown>
        }
        <Typography variant="h4" mt={mobileView ? 3 : 5} mb={-1}>Key details</Typography>
        {sa.activity.detailFields?.volunteersToBring &&
          <>
            <Typography variant="subhead2B" mt={mobileView ? 2.5 : 3.5} mb={mobileView ? .75 : 1.25}>
              What {detailsParticipants(sa)} should know
            </Typography>
            <StyledMarkdown>{sa.activity.detailFields.volunteersToBring}</StyledMarkdown>
          </>
        }
        {sa.activity.detailFields?.safetyMeasures &&
          <>
            <Typography variant="subhead2B" mt={mobileView ? 2.5 : 3.5} mb={mobileView ? .75 : 1.25}>Safety measures</Typography>
            <StyledMarkdown>{sa.activity.detailFields.safetyMeasures}</StyledMarkdown>
          </>
        }
        {sa.additionalDesc &&
          <>
            <Typography variant="subhead2B" mt={mobileView ? 2.5 : 3.5} mb={mobileView ? .75 : 1.25}>Additional details</Typography>
            <StyledMarkdown>{sa.additionalDesc}</StyledMarkdown>
          </>
        }
      </Box>
    </>
  )
}
